import {
  useCallback, useEffect, useRef, useState
} from 'react';

import APIGateway from '@/api';
import { RunCompletedMessage, RunProgressMessage, TERMINAL_RUN_STATUSES } from '@/types/evals';


type RunProgressStatus = 'idle' | 'streaming' | 'done' | 'error';

type UseRunProgressReturn = {
  progress: RunProgressMessage | null;
  status: RunProgressStatus;
  isTerminal: boolean;
};

/**
 * Convert snake_case keys to camelCase for a flat object.
 */
export function snakeToCamel( obj: Record<string, unknown> ): Record<string, unknown> {
  const result: Record<string, unknown> = {};

  for( const [ key, value ] of Object.entries( obj ) ) {
    const camelKey = key.replace( /_([a-z])/g, ( _, c ) => c.toUpperCase() );
    result[ camelKey ] = value;
  }

  return result;
}

/**
 * Check if a run status is terminal.
 */
export function isTerminalStatus( status: string ): boolean {
  return ( TERMINAL_RUN_STATUSES as readonly string[] ).includes( status );
}

/**
 * Parse SSE-formatted data from a text buffer.
 * SSE format: lines prefixed with "data: ", events separated by "\n\n".
 * Returns parsed JSON objects and any remaining unparsed buffer.
 */
export function parseSSEBuffer( buffer: string ): { events: Record<string, unknown>[]; remaining: string } {
  const events: Record<string, unknown>[] = [];
  const parts = buffer.split( '\n\n' );
  const remaining = parts.pop() || '';

  for( const part of parts ) {
    const dataLine = part.split( '\n' ).find( l => l.startsWith( 'data: ' ) );

    if( !dataLine ) continue;

    try {
      const parsed = JSON.parse( dataLine.slice( 6 ) ) as Record<string, unknown>;
      events.push( parsed );
    } catch( e ) {
      console.error( 'Failed to parse SSE data:', e, 'Line:', dataLine );
    }
  }

  return { events, remaining };
}

/**
 * Hook that connects to an SSE stream for a specific run and provides progress state.
 * Only opens a connection when enabled=true and runId is provided.
 * Automatically closes the connection on unmount, when disabled, or when the run reaches a terminal status.
 */
export const useRunProgress = ( runId: string | undefined, enabled: boolean = false ): UseRunProgressReturn => {
  const [ progress, setProgress ] = useState<RunProgressMessage | null>( null );
  const [ status, setStatus ] = useState<RunProgressStatus>( 'idle' );
  const [ isTerminal, setIsTerminal ] = useState<boolean>( false );
  const abortControllerRef = useRef<AbortController | null>( null );

  const processStream = useCallback( async( stream: ReadableStream<Uint8Array>, signal: AbortSignal ) => {
    const reader = stream.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    try {
      while( !signal.aborted ) {
        const { done, value } = await reader.read();

        if( done ) break;

        buffer += decoder.decode( value, { stream: true } );
        const { events, remaining } = parseSSEBuffer( buffer );
        buffer = remaining;

        for( const event of events ) {
          // Check for server-side error
          if( event.completed && event.message ) {
            throw new Error( event.message as string );
          }

          const camelEvent = snakeToCamel( event );
          const runStatus = camelEvent.runStatus as string;

          if( isTerminalStatus( runStatus ) ) {
            setProgress( null );
            setIsTerminal( true );
            setStatus( 'done' );
            return;
          }

          setProgress( camelEvent as unknown as RunProgressMessage );
        }
      }
    } finally {
      reader.releaseLock();
    }
  }, [] );

  useEffect( () => {
    if( !enabled || !runId ) {
      setStatus( 'idle' );
      return;
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;

    setStatus( 'streaming' );
    setIsTerminal( false );
    setProgress( null );

    const startStream = async() => {
      try {
        const stream = await APIGateway.evals.runProgressStream( runId, controller.signal );
        await processStream( stream, controller.signal );
      } catch( e ) {
        if( e instanceof DOMException && e.name === 'AbortError' ) return;

        console.error( 'Run progress stream error:', e );
        setStatus( 'error' );
      }
    };

    startStream();

    return () => {
      controller.abort();
      abortControllerRef.current = null;
    };
  }, [ runId, enabled, processStream ] );

  return { progress, status, isTerminal };
};
